import { eachDayOfInterval, format } from 'date-fns';
import type { AssignmentShiftList } from '../../../../../js/api';
import type { BookedRange } from '../../../components/base-calendar';
import { StatusCodes } from '../../../constants/workflows';
import { parseApiLocalDate } from '../../../utils/date';
import { getShiftStatusBucket } from '../../../utils/globalStyles';

const ISO_DATE = 'yyyy-MM-dd';

/**
 * Compose the calendar's `bookedRanges` for the org create-assignment flow.
 *
 * Two layers, applied in order so the more informative one wins per-day:
 *
 * 1. Own-org shifts (`shifts`): one BookedRange per shift, placed on the
 *    shift's `start_on` only (mirrors the Provider Home calendar). `end_on`
 *    is deliberately ignored — an overnight shift starting Mon 7pm / ending
 *    Tue 7am paints Monday only, because Tuesday morning's bleed doesn't
 *    preclude another shift starting Tuesday evening. Carries the parent
 *    `assignmentId` so click-to-drawer works. Shifts whose status bucket is
 *    null (terminal-negative) or whose `start_on` is unparseable are skipped.
 *
 * 2. Blocked overlay: every day in [windowStart, windowEnd] that is not in
 *    `availableDates` AND not already covered by an own-org shift's start_on
 *    gets a `'blocked'` BookedRange with no assignmentId. The provider's
 *    public availability is the only signal the org has for what's free; the
 *    complement is "not available for some reason" and is visually flagged
 *    so the org rep can't pick those dates.
 *
 * The helper is pure (no React) — easy to mentally test and to unit-test
 * if we later extract a spec.
 */
export function buildBookedRanges(
  shifts: AssignmentShiftList[] | undefined,
  availableDates: string[],
  windowStart: Date,
  windowEnd: Date
): BookedRange[] {
  const ranges: BookedRange[] = [];
  const ownCovered = new Set<string>();

  (shifts ?? []).forEach((shift) => {
    const bucket = getShiftStatusBucket(shift.current_status.code as StatusCodes);
    if (!bucket) return;
    const start = parseApiLocalDate(shift.start_on);
    if (!start) return;
    ranges.push({
      from: start,
      to: start,
      bucket,
      assignmentId: shift.assignment,
    });
    ownCovered.add(format(start, ISO_DATE));
  });

  const availableSet = new Set(availableDates);

  if (windowStart <= windowEnd) {
    eachDayOfInterval({ start: windowStart, end: windowEnd }).forEach((day) => {
      const key = format(day, ISO_DATE);
      if (ownCovered.has(key)) return;
      if (availableSet.has(key)) return;
      ranges.push({
        from: day,
        to: day,
        bucket: 'blocked',
      });
    });
  }

  return ranges;
}
